import {
  property,
  subclass,
} from "@arcgis/core/core/accessorSupport/decorators";
import {
  messageBundle,
  storeNode,
  tsx,
} from "@arcgis/core/widgets/support/widget";
import Widget from "@arcgis/core/widgets/Widget";
import { VNode } from "src/interfaces/interfaces";
import PublishAppViewModel from "../../data/PublishApp/PublishAppViewModel";
import {
  isEnterprise,
  isMobileView,
  keyboardAccessibleHelper,
  sanitizer,
} from "../../utils/appUtils";

import { EAppTemplateType } from "templates-common-library/baseClasses/CompatibilityChecker";
import AppTypeInfoModal from "../AppTypeInfoModal/AppTypeInfoModal";
import AppTypeTileViewModel from "./AppTypeTileViewModel";

import { renderDescription, renderExamples } from "../../utils/appUtils";

import "./styles/AppTypeTile.scss";
import IFrameAppPreview from "../IFrameAppPreview/IFrameAppPreview";
import PublishForm from "../PublishForm/PublishForm";

import CircleDownCarotSVG from "../../../../public/assets/images/Info_icon.svg";
import {
  applyShadowStyle,
  applyTextFade,
  prepRootModal,
} from "../../utils/utils";
import InfoBadge from "../InfoBadge/InfoBadge";
import { ddButtonDef } from "../AppTile/AppTile";
import { focusElement } from "../../utils/focusUtils";
import { SHADOW_STYLE_DROPDOWN } from "../../utils/shadowStyles";
import ContentAreaViewModel from "../../ui/ContentArea/ContentAreaViewModel";

import AppMessages from "../../../t9n/App/App.json";
import { assignPopoverId } from "../PopoverOnboarding/PopoverViewModel";

const INTRINSIC_IMG_SIZE = 1000;

export interface AppTypeInfo {
  thumbnailUrl: string;
  screenshotUrl: string;
  created?: Date;
  badge?: string;
  appTypeTitle: string;
  appTypeUrlFragment: EAppTemplateType; // end url (/apps/instant/3dviewer/index.html)
  appTypeUrl: string; // full url for portal (ex: https://devext.arcgis.com/apps/instant/3dviewer/index.html)
  compatibleMsgs: string | null;
  appTypeId: string; // Id of the App Type's PortalItem
  portalItem: __esri.PortalItem;
}

export interface ResourceInfo {
  resourceItem: resourceItem;
  resourceType: EResourceType;
}

export interface AppTypeTileProperties extends __esri.WidgetProperties {
  uniqueTileId: string;
  appTypeInfo: AppTypeInfo;
  publishAppVM: PublishAppViewModel;
  resourceInfo: ResourceInfo;
  contentAreaVM: ContentAreaViewModel;
  layoutType: { state: "detailed" | "thumbnail" };
}

export enum EResourceType {
  Webmap,
  Webscene,
  Group,
  None,

  Unknown,
}
export type resourceItem = __esri.PortalItem | __esri.PortalGroup;
export type previewType = "samplePreview" | "mapPreview";

@subclass("Landing.widgets.AppTypeTile")
export default class AppTypeTile extends Widget {
  @property()
  identifier: "AppTypeTile" = "AppTypeTile";

  @property()
  uniqueTileId: string;

  @property()
  appTypeInfo: AppTypeInfo;

  @property()
  publishAppVM: PublishAppViewModel;

  @property()
  resourceInfo: ResourceInfo;

  @property()
  contentAreaVM: ContentAreaViewModel;

  @property()
  publishModal: PublishForm;

  @property()
  infoModal: AppTypeInfoModal;

  @property()
  iFramePreviewModal: IFrameAppPreview;

  @property()
  previewButtonDOM: HTMLCalciteButtonElement;

  @property()
  @messageBundle("dist/assets/t9n/widgets/AppTypeTile/AppTypeTile")
  messages;

  @property()
  @messageBundle("dist/assets/t9n/App/App")
  appMessages: typeof AppMessages;

  @property()
  viewModel = new AppTypeTileViewModel();

  @property()
  infoBadge: InfoBadge;

  @property()
  layoutType: { state: "detailed" | "thumbnail" };

  private _previewType: previewType;
  private _hamburgerMenuButtons: ddButtonDef[];

  /** Saved active element when a modal is launched */
  private _activeElement: HTMLElement;

  // assigned near render innovation
  index: number;

  constructor(properties?: AppTypeTileProperties) {
    super(properties);
  }

  postInitialize() {
    const { badge, thumbnailUrl, appTypeUrlFragment } = this.appTypeInfo;
    this._previewType = this._decidePreviewBtnTitle();

    this.appTypeInfo.thumbnailUrl = this._decideThumbnailUrl(thumbnailUrl);
    if (badge && !isEnterprise(this.publishAppVM.portal)) {
      this.infoBadge = new InfoBadge({
        badge,
        appType: appTypeUrlFragment,
        uniqueId: `badge${this.uniqueTileId}`,
      });
    }

    this._initHamburgerMenu();
  }

  render() {
    return this.layoutType.state === "thumbnail"
      ? this._renderThumbnailCardLayout()
      : this._renderDetailedCardLayout();
  }

  private _renderThumbnailCardLayout() {
    const tileImg = this._renderTileImg();
    const titleArea = this._renderTitleArea();
    const buttonArea = this._renderButtonArea();
    const { compatibleMsgs } = this?.appTypeInfo;
    return (
      <div class="app-type-tile" key="app-type-tile-key">
        {titleArea}
        {tileImg}
        {buttonArea}
        {compatibleMsgs != null ? (
          <div id="notcompatMsgs" key="notcompatMsgs">
            <calcite-icon
              id="warning-icon"
              icon="exclamation-mark-triangle-f"
              scale="s"
            ></calcite-icon>
            <div
              key={`notcompatMsg${this.appTypeInfo.appTypeId}`}
              class="warning-text"
            >
              {compatibleMsgs}
            </div>
          </div>
        ) : null}

        {this.publishModal?.render()}
        {this.infoModal?.render()}
      </div>
    );
  }

  private _renderDetailedCardLayout() {
    const {
      appTypeTitle,
      compatibleMsgs,
      appTypeId,
      thumbnailUrl,
      appTypeUrlFragment,
    } = this?.appTypeInfo;
    const descSection = renderDescription(
      appTypeUrlFragment,
      this.appMessages,
      this.publishAppVM.portal
    );
    const exSection = renderExamples(
      appTypeUrlFragment,
      this.appMessages,
      this.publishAppVM.portal
    );

    return (
      <div class="app-type-tile-detailed" key="app-type-tile-detailed-key">
        <div class="title-row">
          <div class="left-title">
            <h3>{appTypeTitle}</h3>
            <div class="badge">
              {this.infoBadge?.renderWrapper(
                "detailed",
                this.contentAreaVM.navbarVM.uniqueRenderingTimeStamp
              )}
            </div>
          </div>
          <div class="right-title">
            {compatibleMsgs != null
              ? [
                  <calcite-icon
                    id="warning-icon-detailed"
                    icon="exclamation-mark-triangle-f"
                    key={`notcompatMsgs-detailed-icon${appTypeId}`}
                    scale="l"
                  ></calcite-icon>,
                  <div
                    key={`notcompatMsgDetailed${appTypeId}`}
                    class="warning-text-detailed"
                  >
                    {compatibleMsgs}
                  </div>,
                ]
              : null}
          </div>
        </div>
        <div class="info-row">
          <div class="app-description-modal__description">
            <img
              class="app-desc-image"
              src={thumbnailUrl}
              width="150px"
              alt=""
            />
            {descSection}
            {exSection}
          </div>
        </div>
        <div class="bottom-row">
          {this._renderIconOptions()}
          {this._renderButtonArea(true)}
        </div>

        {this.publishModal?.render()}
      </div>
    );
  }

  private _initHamburgerMenu() {
    const { viewItemDetails, appDescription } = this.appMessages.hamburgerBtns;
    this._hamburgerMenuButtons = [
      {
        icon: "information",
        label: appDescription,
        onClickFunc: () => {
          this._initializeInfoModal();
        },
      },
      {
        icon: "infographic",
        label: viewItemDetails,
        onClickFunc: () => {
          window.open(
            sanitizer.sanitizeUrl(
              `${this.publishAppVM.orgUrl}/home/item.html?id=${this.appTypeInfo.appTypeId}`
            )
          );
        },
      },
    ];
  }

  private _renderTitleArea() {
    const { appTypeTitle } = this?.appTypeInfo;
    return (
      <div class="app-type-tile__title">
        <div class="left-align">
          <div id="app-title">
            <div>
              <span afterCreate={applyTextFade} title={appTypeTitle}>
                {appTypeTitle}
              </span>
            </div>
          </div>
          {this.infoBadge?.renderWrapper(
            "grid",
            this.contentAreaVM.navbarVM.uniqueRenderingTimeStamp
          )}
        </div>
        {this._renderHamburgerMenu()}
      </div>
    );
  }

  private _renderButtonArea(isDetailedView?: boolean) {
    const isSampleAndInPortal: boolean = this._checkIfSampleAndPortal();
    let noTextButtons = isDetailedView && isMobileView();
    const { appTypeTitle } = this?.appTypeInfo;
    const previewText = this.appMessages.previewButtons[this._previewType];
    return (
      <div class="app-type-tile__buttons">
        {isSampleAndInPortal ? null : (
          <calcite-button
            bind={this}
            afterCreate={storeNode}
            data-node-ref="previewButtonDOM"
            class="previewBtn"
            onclick={this._initializePreview.bind(this)}
            width="full"
            scale="m"
            icon-start={
              this._previewType === "mapPreview"
                ? "camera-flash-on"
                : "analysis"
            }
            appearance="outline"
            label={`${appTypeTitle} - ${previewText}`}
            title={previewText}
          >
            {!noTextButtons && previewText}
          </calcite-button>
        )}
        <calcite-button
          id="chooseBtn"
          class={this.classes(
            "chooseBtn",
            isSampleAndInPortal && "chooseBtn--only"
          )}
          onclick={this._initializePublish.bind(this)}
          width="full"
          scale="m"
          label={`${appTypeTitle} - ${this.messages.chooseBtn}`}
          title={this.messages.chooseBtn}
          icon-start={noTextButtons ? "plus" : null}
        >
          {!noTextButtons && this.messages.chooseBtn}
        </calcite-button>
      </div>
    );
  }

  /** Checks if compat msg exists and the portal is disconnected. If true then the Preview button gets hidden */
  private _checkIfSampleAndPortal(): boolean {
    return (
      this._previewType === "samplePreview" &&
      isEnterprise(this.publishAppVM.portal)
    );
  }

  private _renderTileImg(): VNode {
    const { thumbnailUrl, screenshotUrl, appTypeId } = this.appTypeInfo;
    const imgId = `img-${appTypeId}`;

    return [
      <img
        class={this.classes(
          "app-type-tile__img",
          this.infoBadge != null ? "has-badge" : null
        )}
        id={imgId}
        tabindex={this.infoBadge != null ? "0" : null}
        src={screenshotUrl != null ? screenshotUrl : thumbnailUrl}
      />,
      this.infoBadge != null ? (
        <calcite-tooltip
          class="img-badge-popover"
          key={`img-badge-popover-${appTypeId}`}
          reference-element={imgId}
          placement="top"
        >
          <p>{this.infoBadge?.renderTooltip()}</p>
        </calcite-tooltip>
      ) : null,
    ];
  }

  private _initializePublish() {
    if (this.contentAreaVM?.navbarVM?.isReadOnly) {
      this.contentAreaVM.navbarVM.openReadOnlyWarning();
      return;
    }
    this._saveActiveElem();
    const { appTypeInfo, resourceInfo } = this;
    this.publishModal = new PublishForm({
      ui: "modal",
      mode: "newApp",
      appTypeInfo,
      portal: this.publishAppVM.portal,
      resourceInfo,
      publishAppVM: this.publishAppVM,
      feedbackFunction: (msg: string, success: boolean, appId?: string) => {
        this.contentAreaVM.myAppsDataVM.performQuery();
      },
      sendFocusOnClose: this._focusPriorActiveElem(),
    });
  }

  private _initializeInfoModal() {
    this._saveActiveElem();
    const { thumbnailUrl, appTypeTitle, appTypeUrlFragment } = this.appTypeInfo;
    this.infoModal = new AppTypeInfoModal({
      portal: this.publishAppVM.portal,
      picUrl: thumbnailUrl,
      appTypeTitle: appTypeTitle,
      appTypeUrlFragment: appTypeUrlFragment,
      sendFocusOnClose: this._focusPriorActiveElem(),
    });
  }

  private _initializePreview(): void {
    this._saveActiveElem();
    prepRootModal();
    const { appTypeInfo, resourceInfo, publishAppVM } = this;
    this.iFramePreviewModal = new IFrameAppPreview({
      container: "rootModal",
      appTypeInfo,
      resourceInfo,
      publishAppVM,
      previewType: this._previewType,
      sendFocusOnClose: this._focusPriorActiveElem(),
    });
  }

  private _renderHamburgerMenu(): VNode {
    const { openHamburgerMenu } = this?.appMessages?.hamburgerBtns;
    const { appTypeId } = this?.appTypeInfo;
    const id = `${appTypeId}-dropdown`;
    return (
      <calcite-dropdown
        id={id}
        class="app-type-tile__dropdown-options"
        placement="bottom-leading"
        role="button"
        title={openHamburgerMenu}
        aria-label={openHamburgerMenu}
        type="click"
        scale="m"
        width-scale="m"
      >
        <calcite-button
          slot="trigger"
          scale="m"
          appearance="transparent"
          kind="neutral"
          title={openHamburgerMenu}
          aria-label={openHamburgerMenu}
          icon-start="ellipsis"
          alignment="center"
          width="auto"
          onclick={(e) => {
            e.preventDefault();
            setTimeout(() => {
              // firefox unique bug - still needs a proper fix - https://devtopia.esri.com/WebGIS/arcgis-template-landing/issues/931
              if (navigator.userAgent.toLowerCase().indexOf("firefox") > -1) {
                const compatTilesDiv = document.querySelectorAll(".tiles");
                compatTilesDiv.forEach((div: HTMLDivElement) => {
                    div.style.display = "none";
                    setTimeout(() => {
                      div.style.display = "grid";

                      setTimeout(() => {
                        const dd = document.getElementById(id);
                        dd.scrollIntoView({
                          block: "nearest",
                          inline: "nearest",
                        });
                        const firstItem = dd.querySelector(
                          "calcite-dropdown-item"
                        );
                        firstItem.focus();
                        firstItem.setFocus();
                      }, 200);
                    });
                });
              }
            });
          }}
        ></calcite-button>
        <calcite-dropdown-group
          role="menu"
          selection-mode="none"
          selectionMode="none"
        >
          {this._renderDropDownOptions()}
        </calcite-dropdown-group>
      </calcite-dropdown>
    );
  }

  private _renderDropDownOptions(): VNode[] {
    return this?._hamburgerMenuButtons?.map(
      (option: ddButtonDef, index: number) => {
        const { icon, label, labelHoverText, onClickFunc } = option;
        const ariaName =
          labelHoverText != null
            ? labelHoverText
            : `${this?.appTypeInfo?.appTypeTitle} - ${label}`;
        return (
          <calcite-dropdown-item
            id={
              this.index === 0 && icon === "information"
                ? assignPopoverId(4)
                : null
            }
            key={`${this.appTypeInfo.appTypeId}${index}`}
            afterCreate={(ddItem) =>
              applyShadowStyle(SHADOW_STYLE_DROPDOWN, ddItem)
            }
            title={ariaName}
            aria-label={ariaName}
            icon-start={icon}
            class="appTypeTileDropDownLinks"
            onclick={(e) => {
              e.preventDefault();
              onClickFunc.call(this, e);
            }}
            onkeydown={keyboardAccessibleHelper.bind(
              this,
              onClickFunc.bind(this)
            )}
          >
            {label}
          </calcite-dropdown-item>
        );
      }
    );
  }

  /** Used in detailed view */
  private _renderIconOptions(): VNode | VNode[] {
    const { appTypeId } = this?.appTypeInfo;
    return (
      <div key="icons-to-show" class="detailed-icons">
        {this?._hamburgerMenuButtons
          ?.filter((option) => option.icon !== "information")
          ?.map((option: ddButtonDef) => {
            const { icon, label, labelHoverText, onClickFunc } = option;
            const itemDetailsIdKey = `item-details-id-${appTypeId}-${icon}`;
            const ariaName =
              labelHoverText != null
                ? labelHoverText
                : `${this?.appTypeInfo?.appTypeTitle} - ${label}`;
            return [
              <calcite-fab
                key={itemDetailsIdKey}
                id={itemDetailsIdKey}
                scale="l"
                icon={icon}
                class="item-details-icon"
                kind="brand"
                role="button"
                appearance="outline"
                title={ariaName}
                aria-label={ariaName}
                onclick={onClickFunc.bind(this)}
                tabindex="0"
                onkeyup={(e) =>
                  keyboardAccessibleHelper(onClickFunc.bind(this), e)
                }
              ></calcite-fab>,
              <calcite-tooltip
                key={`${itemDetailsIdKey}-tip`}
                reference-element={itemDetailsIdKey}
                placement="top"
                label={labelHoverText != null ? labelHoverText : label}
              >
                <span>{labelHoverText != null ? labelHoverText : label}</span>
              </calcite-tooltip>,
            ];
          })}
      </div>
    );
  }

  private _decideThumbnailUrl(defaultUrl: string): string {
    return defaultUrl.indexOf("?") !== -1
      ? `${defaultUrl}&w=${INTRINSIC_IMG_SIZE}`
      : `${defaultUrl}?w=${INTRINSIC_IMG_SIZE}`;
  }

  private _decidePreviewBtnTitle(): previewType {
    return this.resourceInfo.resourceItem != null &&
      this.appTypeInfo.compatibleMsgs == null
      ? "mapPreview"
      : "samplePreview";
  }

  private _saveActiveElem() {
    this._activeElement = document.activeElement as HTMLElement;
  }

  private _focusPriorActiveElem(): Function {
    return focusElement.bind(this, this._activeElement);
  }
}
